/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors } from '../../shared/theme/colors';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { styles } from './PosFiscalBuyerSection.styles';
import { consultaPadron } from '../infra/padronAdapter';
import type { PosFiscalBuyerDraft } from '../domain/fiscalTypes';
import { isValidCuitDigits, normalizeCuitDigits } from '../domain/fiscalPresentation';
import { FiscalCustomerCreateSheet } from './FiscalCustomerCreateSheet';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { usePosCheckoutStore } from '../../pos/store/usePosCheckoutStore';

export type PosFiscalBuyerSectionProps = {
  branchId: string | null;
  draft: PosFiscalBuyerDraft;
  onDraftChange: (patch: Partial<PosFiscalBuyerDraft>) => void;
  disabled?: boolean;
};

type BuyerMode = 'cliente' | 'cf' | 'otro_cuit';

function formatCuitDisplay(cuit: string | null | undefined): string | null {
  const raw = (cuit ?? '').replace(/\D/g, '');
  if (raw.length !== 11) return cuit || null;
  return `${raw.slice(0, 2)}-${raw.slice(2, 10)}-${raw.slice(10)}`;
}

export function PosFiscalBuyerSection({
  branchId,
  draft,
  onDraftChange,
  disabled = false,
}: PosFiscalBuyerSectionProps) {
  const [consulting, setConsulting] = useState(false);
  const [consultError, setConsultError] = useState<string | null>(null);
  const [createSheetVisible, setCreateSheetVisible] = useState(false);
  const [lastPadron, setLastPadron] = useState<{
    cuit: string;
    razonSocial: string;
    taxCondition: number | null;
    sandbox?: boolean;
  } | null>(null);

  const saleCustomerId = usePosCheckoutStore((s) => s.saleCustomerId);
  const customerOptions = usePosCheckoutStore((s) => s.customerOptions);
  const selectedCustomer = saleCustomerId
    ? customerOptions.find((c) => c.id === saleCustomerId) ?? null
    : null;
  const customerHasCuit = Boolean(selectedCustomer?.cuit);

  // Determinar modo actual basado en el draft y el cliente seleccionado
  const currentMode: BuyerMode = (() => {
    if (draft.consumidorFinal) return 'cf';
    if (draft.linkedCustomerId && draft.linkedCustomerId === saleCustomerId && customerHasCuit) {
      return 'cliente';
    }
    return 'otro_cuit';
  })();

  useEffect(() => {
    if (disabled) {
      setConsulting(false);
      setConsultError(null);
      setCreateSheetVisible(false);
      setLastPadron(null);
    }
  }, [disabled]);

  const handleSelectMode = (mode: BuyerMode) => {
    if (mode === 'cf') {
      onDraftChange({
        consumidorFinal: true,
        cuit: '',
        razonSocial: 'Consumidor final',
        taxCondition: null,
        linkedCustomerId: null,
      });
    } else if (mode === 'cliente' && selectedCustomer?.cuit) {
      onDraftChange({
        consumidorFinal: false,
        cuit: selectedCustomer.cuit,
        razonSocial: selectedCustomer.businessName || selectedCustomer.name,
        taxCondition: selectedCustomer.taxCondition ?? null,
        linkedCustomerId: selectedCustomer.id,
      });
    } else if (mode === 'otro_cuit') {
      onDraftChange({
        consumidorFinal: false,
        cuit: draft.consumidorFinal || draft.linkedCustomerId === saleCustomerId ? '' : draft.cuit,
        razonSocial: draft.consumidorFinal || draft.linkedCustomerId === saleCustomerId ? '' : draft.razonSocial,
        taxCondition: draft.consumidorFinal ? null : draft.taxCondition,
        linkedCustomerId: null,
      });
    }
  };

  const handleConsultPadron = async () => {
    if (!branchId) {
      setConsultError('Seleccioná una sucursal activa.');
      return;
    }
    if (!isValidCuitDigits(draft.cuit)) {
      setConsultError('Ingresá un CUIT de 11 dígitos.');
      return;
    }
    setConsulting(true);
    setConsultError(null);
    try {
      const result = await consultaPadron(normalizeCuitDigits(draft.cuit), branchId);
      const padron = {
        cuit: result.cuit,
        razonSocial: result.razonSocial,
        taxCondition: result.taxCondition ?? null,
        sandbox: result.sandbox,
      };
      setLastPadron(padron);
      onDraftChange({
        cuit: padron.cuit,
        razonSocial: padron.razonSocial,
        taxCondition: padron.taxCondition,
        consumidorFinal: false,
      });
      // Sugerir crear cliente solo si el CUIT no matchea el cliente seleccionado
      if (!draft.linkedCustomerId) setCreateSheetVisible(true);
    } catch (err: unknown) {
      setConsultError(err instanceof Error ? err.message : 'No se pudo consultar el padrón.');
    } finally {
      setConsulting(false);
    }
  };

  const radioRow = (mode: BuyerMode, label: string, sublabel?: string | null) => (
    <TouchableOpacity
      style={[styles.checkoutFiscalBuyerCfRow, currentMode === mode && { backgroundColor: colors.accent + '15' }]}
      onPress={() => handleSelectMode(mode)}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ selected: currentMode === mode }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flex: 1 }}>
        <View style={{
          width: 18,
          height: 18,
          borderRadius: 9,
          borderWidth: 2,
          borderColor: currentMode === mode ? colors.accent : colors.border,
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {currentMode === mode ? <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: colors.accent }} /> : null}
        </View>
        <View style={{ flex: 1 }}>
          <Text style={posSharedUi.caption}>{label}</Text>
          {sublabel ? (
            <Text style={[posSharedUi.caption, { color: colors.textSecondary, fontSize: 11 }]} numberOfLines={1}>
              {sublabel}
            </Text>
          ) : null}
        </View>
      </View>
    </TouchableOpacity>
  );

  return (
    <View style={[styles.checkoutFiscalSwitchRow, styles.checkoutFiscalBuyerBlock]}>
      <View style={styles.checkoutFiscalSwitchLeading}>
        <Ionicons name="person-outline" size={22} color={colors.accent} />
        <View style={styles.checkoutFiscalSwitchText}>
          <Text style={styles.checkoutFiscalSwitchTitle}>Facturar a</Text>
          <Text style={styles.checkoutFiscalSwitchHint} numberOfLines={2}>
            {customerHasCuit
              ? 'Cliente seleccionado, Consumidor final u otro CUIT.'
              : 'Consumidor final u otro CUIT con datos de ARCA.'}
          </Text>
        </View>
      </View>

      {/* Opción: Cliente seleccionado (solo si tiene CUIT) */}
      {customerHasCuit && selectedCustomer ? (
        radioRow(
          'cliente',
          selectedCustomer.name,
          `${formatCuitDisplay(selectedCustomer.cuit)}`
        )
      ) : null}

      {/* Opción: Consumidor Final */}
      {radioRow('cf', 'Consumidor final')}

      {/* Opción: Otro CUIT */}
      {radioRow('otro_cuit', 'Otro CUIT')}

      {/* Campos para "Otro CUIT" */}
      {currentMode === 'otro_cuit' ? (
        <>
          <Text style={styles.fiscalFieldLabel}>CUIT</Text>
          <NumericTextInput
            kind="digits"
            style={posSharedUi.modalInput}
            value={draft.cuit}
            onChangeText={(cuit) => onDraftChange({ cuit })}
            placeholder="20XXXXXXXXX"
            placeholderTextColor={colors.textSecondary}
            editable={!disabled}
            testID="fiscal.buyer.cuit-input"
          />
          <TouchableOpacity
            style={[posSharedUi.secondaryButton, (consulting || disabled) && { opacity: 0.6 }]}
            onPress={() => void handleConsultPadron()}
            disabled={consulting || disabled}
            accessibilityRole="button"
            accessibilityLabel="Buscar cliente en ARCA por CUIT"
          >
            {consulting ? (
              <ActivityIndicator color={colors.textPrimary} />
            ) : (
              <Text style={posSharedUi.secondaryButtonText}>Buscar en ARCA</Text>
            )}
          </TouchableOpacity>
          {consultError ? <Text style={[posSharedUi.caption, { color: colors.danger }]}>{consultError}</Text> : null}
          {draft.razonSocial && !draft.consumidorFinal ? (
            <Text style={posSharedUi.caption}>
              {draft.razonSocial}
              {draft.linkedCustomerId ? ' · Cliente vinculado' : ' · Cliente eventual'}
              {lastPadron?.sandbox ? ' · Modo prueba, el nombre no es el de ARCA' : ''}
            </Text>
          ) : null}
        </>
      ) : null}

      {/* Resumen para modo "cliente" */}
      {currentMode === 'cliente' && selectedCustomer ? (
        <Text style={posSharedUi.caption}>
          {selectedCustomer.businessName || selectedCustomer.name}
          {formatCuitDisplay(selectedCustomer.cuit) ? ` · ${formatCuitDisplay(selectedCustomer.cuit)}` : ''}
          {' · Cliente vinculado'}
        </Text>
      ) : null}

      <FiscalCustomerCreateSheet
        visible={createSheetVisible}
        padron={lastPadron}
        onClose={() => setCreateSheetVisible(false)}
        onLinked={(customerId) => {
          onDraftChange({ linkedCustomerId: customerId });
          setCreateSheetVisible(false);
        }}
      />
    </View>
  );
}
